import {Component, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {WidgetData} from '../../../../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../../../../core/shared/widget/service/widget.factory';
import {WidgetDataParam} from '../../../../../../../../core/shared/widget/widget-data-param';
import {FilterCriteria} from '../../../../../../../../core/shared/filter/api/filter.criteria';
import {Sorting} from '../../../../../../../../core/shared/misc/sorting';
import {ObjectIdentifierData} from '../../../../../../../../core/shared/basic/object-identifier-data';
import {ObjectIdentifier} from '../../../../../../../../core/shared/basic/object-identifier';
import {CwFkPreset, cwPersistTabParam, cwReadTabParam, cwWidgetToolbarCreate} from '../../shared/cw-case-type-widget.util';
// Row kind + direction of an IoT mapping. Both are preset on the create forms of the two IoT
// tables so neither can produce a row the backend validator is certain to reject.
import {CwFieldMappingKindEnum} from '../../../../../../model/dto/enum/config/cw-field-mapping-kind.enum';
import {CwFieldMappingBehaviourEnum} from '../../../../../../model/dto/enum/config/cw-field-mapping-behaviour.enum';
// [CW-VP-DIM] Betragsregeln je Baustein (05-design-vp-dimension.md §3.1)
import {
    buildCwCaseTypeCoverageAmountRuleTableWidget
} from '../../../../../../widget/cw-case-type-coverage-amount-rule.widget';

/**
 * AI:
 * Status: "in progress"
 * Type: Component
 * SubType: CrudIntegration
 * Reason: "Übersicht" navigation point of the CwCaseType standard view — 15 case-type-scoped
 *   config tabs (Basic data widget + edit, plus table/parent-child master-detail widgets for
 *   business types, fields, former contracts, closure channels, agency numbers, persons, objects,
 *   coverages, document/notification/duration/payment config and the case-wide question sections). Inactive tab bodies are
 *   never rendered (@if on the active tab); each tab's widgets are built lazily on first
 *   activation and cached thereafter (CONFIG-022 lazy-init rule).
 */
@Component({
    selector: 'cw-case-type-overview',
    templateUrl: './cw-case-type-overview.component.html',
    styleUrls: ['./cw-case-type-overview.component.scss'],
    standalone: false
})
export class CwCaseTypeOverviewComponent implements OnInit, OnChanges {

    /** The owning cw.CwCaseType id — the scope of every tab in this navigation point. */
    @Input() caseTypeId: number;

    /** The active Übersicht tab; drives the single-tab @if guards so hidden tabs never render. */
    activeTab: string = 'basic';

    private readonly validTabs = [
        'basic', 'businessTypes', 'fields', 'formerContracts', 'closureChannels', 'generalAgencyNumbers',
        'persons', 'objects', 'coverages', 'documentGenerate', 'documentTypes', 'notifications',
        'durations', 'payment', 'questions'
    ];

    constructor(private route: ActivatedRoute, private router: Router) {
    }

    // --- Tab 1: Basic (Data Widget + edit toggle) ---
    basicDataWidget: WidgetData;
    basicEditWidget: WidgetData;
    editBasic: boolean = false;

    // --- Tab 2: Geschäftsarten ---
    businessTypeWidget: WidgetData;

    // --- Tab 3: Felder ---
    fieldWidget: WidgetData;

    // --- Tab 4: Vorherige Verträge ---
    formerContractTypeWidget: WidgetData;
    formerProductWidget: WidgetData;

    // --- Tab 5: Abschlusskanäle (parent → field child) ---
    closureChannelWidget: WidgetData;
    closureChannelFieldWidget: WidgetData;
    selectedClosureChannelId: number;

    // --- Tab 6: Agenturnummern ---
    generalAgencyNumberWidget: WidgetData;

    // --- Tab 7: Personen (parent → 2 field children) ---
    personWidget: WidgetData;
    personFieldWidget: WidgetData;
    /** Source attributes of the role (cw.CwCaseTypePersonReqField, FK `type`) — the requirement per pm attribute. */
    personReqFieldWidget: WidgetData;
    selectedPersonId: number;

    // --- Tab 8: Objekte (parent → 3 children, object field → its IoT mappings) ---
    objectWidget: WidgetData;
    objectFieldWidget: WidgetData;
    objectInsurableObjectTypeWidget: WidgetData;
    /** IoT field mappings of the selected object field (cw.CwCaseTypeObjectFieldIotField, FK `caseTypeObjectField`). */
    objectFieldIotFieldWidget: WidgetData;
    /**
     * The second row kind of the same entity: definition mappings (cw.CwCaseTypeObjectFieldIotField,
     * FK `caseTypeObject`). Owned by the OBJECT, not by an object field — hence a table of its own.
     */
    objectDefinitionIotFieldWidget: WidgetData;
    selectedObjectId: number;
    selectedObjectFieldId: number;

    // --- Tab 9: Bausteine/Elementarprodukte (parent → 4 children) ---
    coverageWidget: WidgetData;
    coverageFieldWidget: WidgetData;
    coverageObjectWidget: WidgetData;
    coveragePersonWidget: WidgetData;
    /** [CW-VP-DIM] Betragsregeln des gewählten Bausteins (FK caseTypeCoverage). */
    coverageAmountRuleWidget: WidgetData;
    selectedCoverageId: number;

    // --- Tab 10/11: Dokumenterzeugung / Dokumenttypen ---
    documentGenerateWidget: WidgetData;
    documentTypeWidget: WidgetData;

    // --- Tab 12: Benachrichtigungen ---
    notificationTypeWidget: WidgetData;
    notificationGenerateWidget: WidgetData;

    // --- Tab 13: Vertragslaufzeiten ---
    durationWidget: WidgetData;

    // --- Tab 14: Zahlung ---
    paymentFrequencyWidget: WidgetData;
    paymentMethodWidget: WidgetData;

    // --- Tab 15: Fragen (parent → question child) ---
    questionSectionWidget: WidgetData;
    questionWidget: WidgetData;
    selectedQuestionSectionId: number;

    ngOnInit(): void {
        this.activeTab = cwReadTabParam(this.route, this.validTabs, this.activeTab);
        this.buildActiveTab();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['caseTypeId'] && !changes['caseTypeId'].firstChange) {
            this.resetAllWidgets();
            this.buildActiveTab();
        }
    }

    /** Tab switch — activates the requested tab, persists it, and builds its widgets on first visit (cached after). */
    onTabChange(tab: string | number): void {
        this.activeTab = String(tab);
        cwPersistTabParam(this.router, this.route, this.activeTab);
        this.buildActiveTab();
    }

    private buildActiveTab(): void {
        switch (this.activeTab) {
            case 'basic':
                this.buildBasic();
                break;
            case 'businessTypes':
                this.buildBusinessTypes();
                break;
            case 'fields':
                this.buildFields();
                break;
            case 'formerContracts':
                this.buildFormerContracts();
                break;
            case 'closureChannels':
                this.buildClosureChannels();
                break;
            case 'generalAgencyNumbers':
                this.buildGeneralAgencyNumbers();
                break;
            case 'persons':
                this.buildPersons();
                break;
            case 'objects':
                this.buildObjects();
                break;
            case 'coverages':
                this.buildCoverages();
                break;
            case 'documentGenerate':
                this.buildDocumentGenerate();
                break;
            case 'documentTypes':
                this.buildDocumentTypes();
                break;
            case 'notifications':
                this.buildNotifications();
                break;
            case 'durations':
                this.buildDurations();
                break;
            case 'payment':
                this.buildPayment();
                break;
            case 'questions':
                this.buildQuestions();
                break;
        }
    }

    // ============================ Tab builders (lazy, cached) ============================

    /**
     * [CW-PERSON-REFACTOR][E1] The Basisdaten tab — and the home of the address/contact ROUTING since
     * E1 moved those eight attributes here from {@code cw.CwCaseTypePerson}.
     *
     * <h3>Why they did NOT get a tab of their own</h3>
     * The impact analysis proposed one, on the argument that {@code CwCaseTypeDto} already carries
     * ~45 properties and +18 would make the mask unusable. That argument does not survive contact with
     * how the backend declared them: all eight sit in the EXISTING field group
     * {@code FIELD_GROUP_PERSONS} („Personen-Konfiguration“, priority 28), next to the five
     * person-type fields that were already there. The generic form renders a field group as its own
     * labelled section, so the mask gains one section rather than eighteen loose rows — which is
     * precisely what a separate tab would have bought, at the price of a curated widget with
     * {@code MvsFormControlOverwrite} on nine named fields.
     *
     * <p>That price is not theoretical. An {@code access} overwrite is NOT restored when a later build
     * omits the entry: a field stamped HIDDEN once stays hidden. Hand-listing nine field names to
     * reproduce a grouping the backend already declares would be a second, silently drifting copy of
     * the configuration surface.
     *
     * <p>The tab therefore stays generic. What the persons tab gained instead is a German hint saying
     * where the routing now lives — see the template.
     */
    private buildBasic(): void {
        if (this.basicDataWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseType base-data read widget (Übersicht > Basic).
         */
        this.basicDataWidget = WidgetFactory.createWidgetEntityData(
            'cw.case.type.widget.basic',
            'Basisdaten',
            'cw.CwCaseType',
            this.caseTypeId
        );
    }

    /** Toggles the Basic tab between the read data widget and the editable object form. */
    toggleBasicEdit(): void {
        this.editBasic = !this.editBasic;
        if (this.editBasic && !this.basicEditWidget) {
            /**
             * AI: Status "in progress" | Type Widget | SubType Runtime
             * Reason: CwCaseType editable object form (Übersicht > Basic, edit toggle).
             */
            this.basicEditWidget = WidgetFactory.createWidgetObject(
                'cw.CwCaseType.widget.object.edit',
                'Basisdaten bearbeiten',
                'cw.CwCaseType',
                this.caseTypeId
            );
        }
    }

    private buildBusinessTypes(): void {
        if (this.businessTypeWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeBusinessType table scoped to the case type (Übersicht > Geschäftsarten).
         */
        this.businessTypeWidget = this.caseTypeTable(
            'cw.CwCaseTypeBusinessType.widget.basic.new',
            'Geschäftsarten',
            'cw.CwCaseTypeBusinessType',
            'Keine Geschäftsarten vorhanden'
        );
    }

    private buildFields(): void {
        if (this.fieldWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeField table (FK type) scoped to the case type (Übersicht > Felder).
         */
        this.fieldWidget = WidgetFactory.createWidgetTableEntityQl(
            'cw.CwCaseTypeField.widget.basic.new',
            'Felder',
            'cw.CwCaseTypeField',
            'Keine Felder vorhanden',
            [FilterCriteria.create('e.typeId', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            [new Sorting('e.fieldOrder', true)],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        this.fieldWidget.functionCallbacks = cwWidgetToolbarCreate([{field: 'typeDtoId', value: this.caseTypeId}]);
    }

    private buildFormerContracts(): void {
        if (this.formerContractTypeWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeFormerContractType table scoped to the case type (Übersicht > Vorherige Verträge).
         */
        this.formerContractTypeWidget = this.caseTypeTable(
            'cw.CwCaseTypeFormerContractType.widget.basic.new',
            'Vorvertragstypen',
            'cw.CwCaseTypeFormerContractType',
            'Keine Vorvertragstypen vorhanden'
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeFormerProduct table scoped to the case type (Übersicht > Vorherige Verträge).
         */
        this.formerProductWidget = this.caseTypeTable(
            'cw.CwCaseTypeFormerProduct.widget.basic.new',
            'Vorprodukte',
            'cw.CwCaseTypeFormerProduct',
            'Keine Vorprodukte vorhanden'
        );
    }

    private buildClosureChannels(): void {
        if (this.closureChannelWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeClosureChannel selectable master (Übersicht > Abschlusskanäle).
         */
        this.closureChannelWidget = this.caseTypeSelectable(
            'cw.CwCaseTypeClosureChannel.widget.basic.new',
            'Abschlusskanäle',
            'cw.CwCaseTypeClosureChannel',
            'Keine Abschlusskanäle vorhanden'
        );
    }

    handleClosureChannelSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const channelId = this.selectedId(event);
        if (!channelId) {
            return;
        }
        this.selectedClosureChannelId = channelId;
        this.closureChannelFieldWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeClosureChannelField table (FK type) for the selected closure channel.
         */
        this.closureChannelFieldWidget = this.typeChildTable(
            'cw.CwCaseTypeClosureChannelField.widget.basic.new',
            'Kanalfelder',
            'cw.CwCaseTypeClosureChannelField',
            'Keine Kanalfelder vorhanden',
            channelId
        );
    }

    /**
     * Übersicht &gt; Agenturnummern — the {@code cm.GeneralAgencyNumber} entries (Vermittlernummern)
     * this case type may submit under.
     *
     * Sits directly next to Abschlusskanäle because {@code caseTypeClosureChannel} is the row's
     * OPTIONAL narrowing axis: set = the number is offered only inside that closure channel, empty =
     * it applies to the whole case type. The rows themselves hang off {@code caseType}, so this is a
     * FLAT tab and deliberately not a detail list of the closure-channel master — a channel-scoped
     * list could not show the (unrestricted) rows that carry no channel at all.
     */
    private buildGeneralAgencyNumbers(): void {
        if (this.generalAgencyNumberWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeGeneralAgencyNumber table scoped to the case type (Übersicht > Agenturnummern).
         */
        this.generalAgencyNumberWidget = this.caseTypeTable(
            'cw.CwCaseTypeGeneralAgencyNumber.widget.basic.new',
            'Agenturnummern',
            'cw.CwCaseTypeGeneralAgencyNumber',
            'Keine Agenturnummern vorhanden',
            new Sorting('e.orderPriority', true)
        );
    }

    /**
     * [CW-PERSON-REFACTOR] The case type's ROLE catalogue — "which roles does this Vorgangstyp know"
     * (Versicherungsnehmer, versicherte Person, Beitragszahler, …). Untouched by the refactor: both the
     * entity and this tab survive.
     *
     * <p>Do not confuse the role with the product/role LINK {@code cw.CwCaseTypeProductPerson} ("which
     * of my products offers this role"), which is maintained under Produkte > Personen, nor with the
     * runtime occupancy {@code cw.CwCaseProductPerson} ("this human holds this role in this line").
     */
    private buildPersons(): void {
        if (this.personWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypePerson selectable master (Übersicht > Personen).
         */
        this.personWidget = this.caseTypeSelectable(
            'cw.CwCaseTypePerson.widget.basic.new',
            'Personen',
            'cw.CwCaseTypePerson',
            'Keine Personen vorhanden'
        );
    }

    /**
     * Child table of the selected role: {@code cw.CwCaseTypePersonField} (FK {@code type}).
     *
     * <h3>[CW-PERSON-REFACTOR] What this table means NOW — the doc it used to carry is wrong</h3>
     * It is no longer "the extension fields of a case person". The case-level value bag that gave it
     * that reading, {@code cw.CwCasePersonField}, was DELETED with the refactor and {@code cw.CwCasePerson}
     * is no longer a template instance at all.
     *
     * <p>What this table is instead: the FIELD POOL of the PRODUCT-level occupancy
     * {@code cw.CwCaseProductPerson}, whose template type is the role selected on the left
     * ({@code cw.CwCaseTypePerson}) and whose values are written to {@code cw.CwCaseProductPersonField}.
     * So a definition entered here is authored ONCE on the role and applies in EVERY product line in
     * which that role is occupied — which is why the product-side Personen drill deliberately has no
     * field table of its own (FD-6; the orphaned {@code cw.CwCaseTypeProductPersonField} pool was
     * removed together with it).
     *
     * <p>The label stays "Personenfelder" — from the configurator's point of view nothing moved; only
     * the entity that consumes the definitions did.
     */
    handlePersonSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const personId = this.selectedId(event);
        if (!personId) {
            return;
        }
        this.selectedPersonId = personId;
        this.personFieldWidget = null;
        this.personReqFieldWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypePersonField table (FK type) — the field POOL of the role, consumed by the
         *   product-level occupancy cw.CwCaseProductPerson (Übersicht > Personen).
         */
        this.personFieldWidget = this.typeChildTable(
            'cw.CwCaseTypePersonField.widget.basic.new',
            'Personenfelder',
            'cw.CwCaseTypePersonField',
            'Keine Personenfelder vorhanden',
            personId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypePersonReqField table (FK type) — the SOURCE-ATTRIBUTE requirement list of the
         *   role: which pm.Person / pm.PersonDetail properties are demanded (and optionally published as
         *   pm_<alias>). Sibling of the generic field pool, not a subset of it (Übersicht > Personen).
         */
        this.personReqFieldWidget = this.typeChildTable(
            'cw.CwCaseTypePersonReqField.widget.basic.new',
            'Pflichtangaben (Quellattribute)',
            'cw.CwCaseTypePersonReqField',
            'Keine Quellattribute konfiguriert',
            personId
        );
    }

    private buildObjects(): void {
        if (this.objectWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeObject selectable master (Übersicht > Objekte).
         */
        this.objectWidget = this.caseTypeSelectable(
            'cw.CwCaseTypeObject.widget.basic.new',
            'Objekte',
            'cw.CwCaseTypeObject',
            'Keine Objekte vorhanden'
        );
    }

    handleObjectSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const objectId = this.selectedId(event);
        if (!objectId) {
            return;
        }
        this.selectedObjectId = objectId;
        this.objectFieldWidget = null;
        this.objectInsurableObjectTypeWidget = null;
        // The target-field mappings hang on the selected OBJECT FIELD, not on the object — when the
        // object changes the previous field selection is void and the drilldown has to fall back.
        this.objectFieldIotFieldWidget = null;
        this.selectedObjectFieldId = null;
        this.objectDefinitionIotFieldWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeObjectField table (FK type) for the selected object.
         */
        this.objectFieldWidget = this.typeChildTable(
            'cw.CwCaseTypeObjectField.widget.basic.new',
            'Objektfelder',
            'cw.CwCaseTypeObjectField',
            'Keine Objektfelder vorhanden',
            objectId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeObjectInsurableObjectType junction (FK caseTypeObject) for the selected object.
         */
        this.objectInsurableObjectTypeWidget = this.childTable(
            'cw.CwCaseTypeObjectInsurableObjectType.widget.basic.new',
            'Versicherbare Objektarten',
            'cw.CwCaseTypeObjectInsurableObjectType',
            'Keine Objektarten zugeordnet',
            'caseTypeObject',
            objectId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeObjectFieldIotField table in its DEFINITION shape (FK caseTypeObject) — the
         *   mappings that occupy no case-type field and publish their source value as io_<alias>
         *   (Übersicht > Objekte).
         */
        // The second row kind of the same entity, hence a table of its own: a DEFINITION row hangs on the
        // OBJECT (`caseTypeObject`), a TARGET_FIELD row on the object field. The filter deliberately uses
        // the owner FK and NOT `mappingKind` — V390 added `mapping_kind` as a bare nullable SMALLINT with
        // no DEFAULT and no back-fill, so pre-existing rows on clone/prod hold NULL and a filter on the
        // discriminator would make them invisible.
        //
        // Both presets are mandatory, not convenience:
        //   - mappingKind = DEFINITION, because the entity default is TARGET_FIELD. Without the preset a
        //     row created here would declare itself a target-field mapping while the FK it carries is the
        //     object — exactly the contradiction CwObjectSourceValidationSupport rejects.
        //   - mappingBehaviour = DEFAULT, because the column is NOT NULL and rule V2' demands DEFAULT on a
        //     DEFINITION row: there is no cw field for BIND to write back to.
        //
        // Its own alias VARIANT (`definition` instead of `basic`), not merely a different title: both
        // tables are visible at the same time and the alias keys the per-user UiWidgetLayout config — with
        // a shared alias, curating one table's column list would silently re-arrange the other. Segment
        // order is `widget.{variant}.{mode}`; the product-scoped twin uses the same variant name for the
        // same row kind.
        this.objectDefinitionIotFieldWidget = this.childTable(
            'cw.CwCaseTypeObjectFieldIotField.widget.definition.new',
            'Definitions-Zuordnungen',
            'cw.CwCaseTypeObjectFieldIotField',
            'Keine Definitions-Zuordnungen vorhanden',
            'caseTypeObject',
            objectId,
            [
                {field: 'mappingKind', value: CwFieldMappingKindEnum.DEFINITION},
                {field: 'mappingBehaviour', value: CwFieldMappingBehaviourEnum.DEFAULT}
            ]
        );
    }

    /**
     * Drilldown of the object-field table: {@code cw.CwCaseTypeObjectFieldIotField} (FK
     * {@code caseTypeObjectField}) — which {@code ci.InsurableObjectTypeField} is mirrored into this
     * case-type field, per concrete insurable object type.
     *
     * <p>The master here is a TABLE, not a selectable, so the event carries the row id in
     * {@code objectId} rather than in {@code data['e.id']}; {@link #selectedId} covers both shapes.
     *
     * <p>READ side: only {@code TARGET_FIELD} rows are listed. {@code DEFINITION} rows hang on the
     * second, alternative owner {@code caseTypeObject} and are deliberately absent from this filtered
     * list — they have their own table on the object master.
     *
     * <p>WRITE side: the filter alone does NOT keep this table's create dialog on the target-field
     * shape. Without an explicit stamp the configurator could save a row that sets
     * {@code caseTypeObjectField} AND declares itself {@code DEFINITION} — the exact contradiction
     * {@code CwObjectSourceValidationSupport} rejects. Hence {@code mappingKind} is preset here too,
     * symmetrically to the definition table.
     */
    handleObjectFieldSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const objectFieldId = this.selectedId(event);
        if (!objectFieldId) {
            return;
        }
        this.selectedObjectFieldId = objectFieldId;
        this.objectFieldIotFieldWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeObjectFieldIotField table (FK caseTypeObjectField) — the insurable-object
         *   source-field mappings of the selected case-type object field (Übersicht > Objekte).
         */
        this.objectFieldIotFieldWidget = this.childTable(
            'cw.CwCaseTypeObjectFieldIotField.widget.basic.new',
            'Zielfeld-Zuordnungen',
            'cw.CwCaseTypeObjectFieldIotField',
            'Keine Zielfeld-Zuordnungen vorhanden',
            'caseTypeObjectField',
            objectFieldId,
            // Stamped even though it matches the entity default: the default protects a row nobody
            // edited, not one whose kind select the configurator can still change in the create dialog.
            [{field: 'mappingKind', value: CwFieldMappingKindEnum.TARGET_FIELD}]
        );
    }

    private buildCoverages(): void {
        if (this.coverageWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeCoverage selectable master (Übersicht > Bausteine/Elementarprodukte).
         */
        this.coverageWidget = this.caseTypeSelectable(
            'cw.CwCaseTypeCoverage.widget.basic.new',
            'Bausteine',
            'cw.CwCaseTypeCoverage',
            'Keine Bausteine vorhanden'
        );
    }

    handleCoverageSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const coverageId = this.selectedId(event);
        if (!coverageId) {
            return;
        }
        this.selectedCoverageId = coverageId;
        this.coverageFieldWidget = null;
        this.coverageObjectWidget = null;
        this.coveragePersonWidget = null;
        this.coverageAmountRuleWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeCoverageField table (FK type) for the selected coverage.
         */
        this.coverageFieldWidget = this.typeChildTable(
            'cw.CwCaseTypeCoverageField.widget.basic.new',
            'Bausteinfelder',
            'cw.CwCaseTypeCoverageField',
            'Keine Bausteinfelder vorhanden',
            coverageId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeCoverageObject junction (FK caseTypeCoverage) for the selected coverage.
         */
        this.coverageObjectWidget = this.childTable(
            'cw.CwCaseTypeCoverageObject.widget.basic.new',
            'Baustein-Objekte',
            'cw.CwCaseTypeCoverageObject',
            'Keine Objekte zugeordnet',
            'caseTypeCoverage',
            coverageId
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeCoveragePerson junction (FK caseTypeCoverage) for the selected coverage.
         */
        this.coveragePersonWidget = this.childTable(
            'cw.CwCaseTypeCoveragePerson.widget.basic.new',
            'Baustein-Personen',
            'cw.CwCaseTypeCoveragePerson',
            'Keine Personen zugeordnet',
            'caseTypeCoverage',
            coverageId
        );
        // [CW-VP-DIM] Betragsregeln (FK caseTypeCoverage) des gewählten Bausteins — die
        // Min/Max/Schrittweite-Grenzen, gegen die das Backend validiert und aus denen das
        // Erfassungs-FE seine Hinweise rendert. Curated-Column-Widget, deshalb aus dem gemeinsamen
        // Builder statt aus childTable().
        this.coverageAmountRuleWidget = buildCwCaseTypeCoverageAmountRuleTableWidget({
            caseTypeCoverageId: coverageId
        });
        this.coverageAmountRuleWidget.functionCallbacks = cwWidgetToolbarCreate([
            {field: 'caseTypeCoverageDtoId', value: coverageId}
        ]);
    }

    private buildDocumentGenerate(): void {
        if (this.documentGenerateWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeDocumentGenerate table scoped to the case type (Übersicht > Dokumenterzeugung).
         */
        this.documentGenerateWidget = this.caseTypeTable(
            'cw.CwCaseTypeDocumentGenerate.widget.basic.new',
            'Dokumenterzeugung',
            'cw.CwCaseTypeDocumentGenerate',
            'Keine Dokumenterzeugung konfiguriert',
            new Sorting('e.orderPriority', true)
        );
    }

    private buildDocumentTypes(): void {
        if (this.documentTypeWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeDocumentType table scoped to the case type (Übersicht > Dokumenttypen).
         */
        this.documentTypeWidget = this.caseTypeTable(
            'cw.CwCaseTypeDocumentType.widget.basic.new',
            'Dokumenttypen',
            'cw.CwCaseTypeDocumentType',
            'Keine Dokumenttypen vorhanden',
            new Sorting('e.orderPriority', true)
        );
    }

    private buildNotifications(): void {
        if (this.notificationTypeWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeNotificationType table scoped to the case type (Übersicht > Benachrichtigungen).
         */
        this.notificationTypeWidget = this.caseTypeTable(
            'cw.CwCaseTypeNotificationType.widget.basic.new',
            'Benachrichtigungstypen',
            'cw.CwCaseTypeNotificationType',
            'Keine Benachrichtigungstypen vorhanden',
            new Sorting('e.orderPriority', true)
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeNotificationGenerate table scoped to the case type (Übersicht > Benachrichtigungen).
         */
        this.notificationGenerateWidget = this.caseTypeTable(
            'cw.CwCaseTypeNotificationGenerate.widget.basic.new',
            'Benachrichtigungs-Generierung',
            'cw.CwCaseTypeNotificationGenerate',
            'Keine Benachrichtigungs-Generierung konfiguriert',
            new Sorting('e.orderPriority', true)
        );
    }

    /**
     * Übersicht &gt; Vertragslaufzeiten — the case-type-wide duration value list.
     *
     * Sits next to Zahlung because both hold the selectable TERMS of the new contract (how long it runs,
     * how it is paid), and deliberately NOT under Vorherige Verträge, which is about the contract being
     * replaced. The per-product variant ({@code cw.CwCaseTypeProductDuration}) hangs off
     * {@code caseTypeProduct}, so its home is Produkte &gt; Vertragslaufzeiten — a widget here could not
     * filter over a join axis this scope does not have.
     */
    private buildDurations(): void {
        if (this.durationWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeDuration table scoped to the case type (Übersicht > Vertragslaufzeiten).
         */
        this.durationWidget = this.caseTypeTable(
            'cw.CwCaseTypeDuration.widget.basic.new',
            'Vertragslaufzeiten',
            'cw.CwCaseTypeDuration',
            'Keine Vertragslaufzeiten vorhanden',
            new Sorting('e.orderPriority', true)
        );
    }

    private buildPayment(): void {
        if (this.paymentFrequencyWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypePaymentFrequency table scoped to the case type (Übersicht > Zahlung).
         */
        this.paymentFrequencyWidget = this.caseTypeTable(
            'cw.CwCaseTypePaymentFrequency.widget.basic.new',
            'Zahlweisen',
            'cw.CwCaseTypePaymentFrequency',
            'Keine Zahlweisen vorhanden'
        );
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypePaymentMethod table scoped to the case type (Übersicht > Zahlung).
         */
        this.paymentMethodWidget = this.caseTypeTable(
            'cw.CwCaseTypePaymentMethod.widget.basic.new',
            'Zahlungsarten',
            'cw.CwCaseTypePaymentMethod',
            'Keine Zahlungsarten vorhanden'
        );
    }

    /**
     * Übersicht > Fragen — the CASE-WIDE question sections only.
     *
     * Product-side sections ({@code cw.CwCaseTypeProductQuestionSection}) are NOT shown here: they hang
     * off {@code caseTypeProduct}, so a widget in this tab would have to filter over a join axis this
     * scope does not have. Their home is Produkte &gt; Fragen.
     */
    private buildQuestions(): void {
        if (this.questionSectionWidget) {
            return;
        }
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeQuestionSection selectable master (Übersicht > Fragen).
         */
        this.questionSectionWidget = this.caseTypeSelectable(
            'cw.CwCaseTypeQuestionSection.widget.basic.new',
            'Fragensektionen',
            'cw.CwCaseTypeQuestionSection',
            'Keine Fragensektionen vorhanden'
        );
    }

    handleQuestionSectionSelect(event: ObjectIdentifierData | ObjectIdentifier): void {
        const sectionId = this.selectedId(event);
        if (!sectionId) {
            return;
        }
        this.selectedQuestionSectionId = sectionId;
        this.questionWidget = null;
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: CwCaseTypeQuestion table (FK section) for the selected question section.
         */
        // The FK relation is 'section' — it was renamed with the entity; 'questionGroup' no longer
        // exists and would filter on nothing, i.e. an empty child list without an error.
        this.questionWidget = this.childTable(
            'cw.CwCaseTypeQuestion.widget.basic.new',
            'Fragen',
            'cw.CwCaseTypeQuestion',
            'Keine Fragen vorhanden',
            'section',
            sectionId
        );
    }

    // ============================ Widget helpers ============================

    /** Generic table-widget builder shared by every FK-`caseType`-scoped Übersicht tab. */
    private caseTypeTable(alias: string, name: string, entity: string, noData: string, sorting?: Sorting): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Case-type-scoped list table (FK caseType) for an Übersicht tab; the entity/alias are supplied by the calling tab builder.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            sorting ? [sorting] : [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeDtoId', value: this.caseTypeId}]);
        return widget;
    }

    /** Generic selectable-master builder shared by every FK-`caseType`-scoped Übersicht tab. */
    private caseTypeSelectable(alias: string, name: string, entity: string, noData: string): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Case-type-scoped selectable master (FK caseType) for an Übersicht parent list; the entity/alias are supplied by the calling tab builder.
         */
        const widget = WidgetFactory.createWidgetSelectableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create('e.caseTypeId', FilterCriteria.cOperatorEqual, this.caseTypeId)],
            [],
            WidgetDataParam.create('attributeToDisplay', 'e.name')
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([{field: 'caseTypeDtoId', value: this.caseTypeId}]);
        return widget;
    }

    /** A child table on the GT-field pattern (FK `type`), scoped + create-preset to the selected parent. */
    private typeChildTable(alias: string, name: string, entity: string, noData: string, parentId: number): WidgetData {
        return this.childTable(alias, name, entity, noData, 'type', parentId);
    }

    /**
     * A child table scoped to a selected parent via an arbitrary FK relation name, with a matching create preset.
     *
     * @param extraPresets further attributes to pre-fill READ-only on the create form, next to the FK.
     *   Needed when a table shows only ONE variant of a multi-shaped entity: the row's discriminator has to
     *   be stamped on creation, otherwise the entity default produces a row whose kind contradicts the
     *   owner FK this table filters on.
     */
    private childTable(alias: string, name: string, entity: string, noData: string, fkRelation: string,
                       parentId: number, extraPresets: CwFkPreset[] = []): WidgetData {
        /**
         * AI: Status "in progress" | Type Widget | SubType Runtime
         * Reason: Child list table scoped to a selected parent (FK <fkRelation>) for an Übersicht master-detail tab; the entity/alias/relation are supplied by the calling selection handler.
         */
        const widget = WidgetFactory.createWidgetTableEntityQl(
            alias, name, entity, noData,
            [FilterCriteria.create(`e.${fkRelation}Id`, FilterCriteria.cOperatorEqual, parentId)],
            [],
            // resolveFkeyTexts: render foreign keys as their display text instead of the raw id.
            true
        );
        widget.functionCallbacks = cwWidgetToolbarCreate([
            {field: `${fkRelation}DtoId`, value: parentId},
            ...extraPresets
        ]);
        return widget;
    }

    /**
     * Reads the selected row id from a widget selection event.
     *
     * <p>Two widget types feed these handlers and they carry the id differently. A SELECTABLE emits the
     * QL row itself, so the projection key `e.id` is right there in `data`. A TABLE emits
     * `data = {row, dto}` — the projection key sits one level down and `data['e.id']` is `undefined`,
     * which would silently abort the handler. Its `objectId` is already the resolved `e.id`, so it is the
     * fallback. Order matters only for readability; both paths yield the same number.
     */
    private selectedId(event: ObjectIdentifierData | ObjectIdentifier): number {
        const data = (event as ObjectIdentifierData)?.data;
        return data?.['e.id'] ?? data?.['dto']?.['e.id'] ?? (event as ObjectIdentifierData)?.objectId;
    }

    private resetAllWidgets(): void {
        this.basicDataWidget = null;
        this.basicEditWidget = null;
        this.editBasic = false;
        this.businessTypeWidget = null;
        this.fieldWidget = null;
        this.formerContractTypeWidget = null;
        this.formerProductWidget = null;
        this.closureChannelWidget = null;
        this.closureChannelFieldWidget = null;
        this.selectedClosureChannelId = null;
        this.generalAgencyNumberWidget = null;
        this.personWidget = null;
        this.personFieldWidget = null;
        this.personReqFieldWidget = null;
        this.selectedPersonId = null;
        this.objectWidget = null;
        this.objectFieldWidget = null;
        this.objectInsurableObjectTypeWidget = null;
        this.objectFieldIotFieldWidget = null;
        this.objectDefinitionIotFieldWidget = null;
        this.selectedObjectId = null;
        this.selectedObjectFieldId = null;
        this.coverageWidget = null;
        this.coverageFieldWidget = null;
        this.coverageObjectWidget = null;
        this.coveragePersonWidget = null;
        this.coverageAmountRuleWidget = null;
        this.selectedCoverageId = null;
        this.documentGenerateWidget = null;
        this.documentTypeWidget = null;
        this.notificationTypeWidget = null;
        this.notificationGenerateWidget = null;
        this.durationWidget = null;
        this.paymentFrequencyWidget = null;
        this.paymentMethodWidget = null;
        this.questionSectionWidget = null;
        this.questionWidget = null;
        this.selectedQuestionSectionId = null;
    }
}
